import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { useChatbotStore } from '../stores/chatbotStore';
import { useAuth } from './AuthContext';

type ChatbotContextType = {
  chatbotPublicId: string | null;
  chatbotUserId: string | null;
  isLoading: boolean;
  error: Error | null;
  refetchChatbot: () => Promise<void>;
};

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

type ChatbotProviderProps = {
  children: ReactNode;
  isWidget?: boolean;
  widgetPublicId?: string;
};

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({ 
  children, 
  isWidget = false, 
  widgetPublicId 
}) => {
  const { user } = useAuth();
  const { 
    chatbot, 
    isLoading, 
    error, 
    fetchChatbotByPublicId, 
    fetchDefaultChatbotByUserId,
    reset
  } = useChatbotStore();

  useEffect(() => {
    const initializeChatbot = async () => {
      if (isWidget && widgetPublicId) {
        // Widget mode: fetch chatbot by public ID
        await fetchChatbotByPublicId(widgetPublicId);
      } else if (user) {
        // Normal mode: fetch default chatbot for the user
        await fetchDefaultChatbotByUserId(user.id);
      }
    };

    initializeChatbot();
    
    // Reset chatbot state when component unmounts
    return () => {
      reset();
    };
  }, [isWidget, widgetPublicId, user, fetchChatbotByPublicId, fetchDefaultChatbotByUserId, reset]);
  

  const refetchChatbot = async () => {
    if (isWidget && widgetPublicId) {
      await fetchChatbotByPublicId(widgetPublicId);
    } else if (user) {
      await fetchDefaultChatbotByUserId(user.id);
    }
  };
  
  const value: ChatbotContextType = {
    chatbotPublicId: chatbot?.public_id ?? widgetPublicId ?? null,
    chatbotUserId: chatbot?.user_id ?? null,
    isLoading,
    error,
    refetchChatbot,
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = (): ChatbotContextType => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};